import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { useToast } from '../hooks/use-toast';
import { useLanguage } from '../contexts/LanguageContext';
import { contactInfo } from '../mock';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const Contact = () => {
  const { toast } = useToast();
  const { t, language } = useLanguage();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    vehicle: '',
    registration: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/contact`, {
        ...formData,
        language: language
      });
      
      toast({
        title: t.contact.success,
        description: response.data.message || t.contact.successMessage,
      });

      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        vehicle: '',
        registration: '',
        message: ''
      });
    } catch (error) {
      console.error('Contact form error:', error);
      toast({
        title: t.contact.error,
        description: error.response?.data?.detail || t.contact.errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-slate-50">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-800 mb-4">
            {t.contact.title}
          </h2>
          <p className="text-lg text-slate-600">
            {t.contact.subtitle}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Contact Information */}
          <div className="space-y-8">
            <div>
              <h3 className="text-2xl font-bold text-slate-800 mb-6">
                {t.contact.infoTitle}
              </h3>
              <p className="text-slate-600 mb-8">
                {t.contact.infoSubtitle}
              </p>
            </div>

            {/* Contact Details */}
            <div className="space-y-6">
              <div className="flex gap-4 items-start group">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-red-600 transition-colors duration-300">
                  <Phone className="text-red-600 group-hover:text-white transition-colors duration-300" size={24} />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-800 mb-1">{t.contact.phone}</h4>
                  <a href={`tel:${contactInfo.phone}`} className="text-slate-600 hover:text-red-600 transition-colors">
                    {contactInfo.phone}
                  </a>
                </div>
              </div>

              <div className="flex gap-4 items-start group">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-red-600 transition-colors duration-300">
                  <Mail className="text-red-600 group-hover:text-white transition-colors duration-300" size={24} />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-800 mb-1">{t.contact.email}</h4>
                  <a href={`mailto:${contactInfo.email}`} className="text-slate-600 hover:text-red-600 transition-colors">
                    {contactInfo.email}
                  </a>
                </div>
              </div>

              <div className="flex gap-4 items-start group">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-red-600 transition-colors duration-300">
                  <MapPin className="text-red-600 group-hover:text-white transition-colors duration-300" size={24} />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-800 mb-1">{t.contact.address}</h4>
                  <p className="text-slate-600">{contactInfo.address}</p>
                </div>
              </div>

              <div className="flex gap-4 items-start group">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-red-600 transition-colors duration-300">
                  <Clock className="text-red-600 group-hover:text-white transition-colors duration-300" size={24} />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-800 mb-1">{t.contact.hours}</h4>
                  <p className="text-slate-600">{contactInfo.hours}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <h3 className="text-2xl font-bold text-slate-800 mb-6">
              {t.contact.formTitle}
            </h3>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="name" className="text-slate-700 font-medium mb-2">
                  {t.contact.form.nameRequired}
                </Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  placeholder={t.contact.form.namePlaceholder}
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="email" className="text-slate-700 font-medium mb-2">
                  {t.contact.form.emailRequired}
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  placeholder={t.contact.form.emailPlaceholder}
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="phone" className="text-slate-700 font-medium mb-2">
                  {t.contact.form.phone}
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder={t.contact.form.phonePlaceholder}
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="vehicle" className="text-slate-700 font-medium mb-2">
                  {t.contact.form.vehicle}
                </Label>
                <Input
                  id="vehicle"
                  name="vehicle"
                  type="text"
                  value={formData.vehicle}
                  onChange={handleChange}
                  placeholder={t.contact.form.vehiclePlaceholder}
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="registration" className="text-slate-700 font-medium mb-2">
                  {t.contact.form.registration}
                </Label>
                <Input
                  id="registration"
                  name="registration"
                  type="text"
                  value={formData.registration}
                  onChange={handleChange}
                  placeholder={t.contact.form.registrationPlaceholder}
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="message" className="text-slate-700 font-medium mb-2">
                  {t.contact.form.messageRequired}
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  required
                  value={formData.message}
                  onChange={handleChange}
                  placeholder={t.contact.form.messagePlaceholder}
                  rows={4}
                  className="mt-1"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-red-600 hover:bg-red-700 text-white py-6 text-lg transition-all hover:scale-105"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    {t.contact.form.sending}
                  </>
                ) : (
                  <>
                    {t.contact.form.submit}
                    <Send className="ml-2 h-5 w-5" />
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};
